import React, { JSX, useCallback, useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  IconButton,
  Rating,
  Link,
  Divider,
  CardContent,
  Button,
} from "@mui/material";
import axios from "../../api";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryModal from "./HistoryModal";
import FormatAssigneeHistory from "examples/ReuseFunctions/FormatAssigneeHistory";
import AiAssignmentBadge from "examples/ReuseFunctions/AiAssignmentBadge";
import FormatStatusHistoryData from "examples/ReuseFunctions/FormatStatusHistory";
import { useSelector } from "react-redux";
import UploadIcon from "@mui/icons-material/Upload";
import LocationDisplay from "components/LocationDisplay";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FormatStatusTrackerData from "examples/ReuseFunctions/FormatStatusTracker";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import dayjs from "dayjs";
import { RootState } from "store";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
interface ProductCellProps {
  value: string;
  sparePart: string;
}

export const ProductCell: React.FC<ProductCellProps> = ({
  value,
  sparePart,
}) => (
  <Box>
    <Typography variant="subtitle2" fontWeight="bold">
      {value}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {sparePart}
    </Typography>
  </Box>
);
interface ProductCellRendererProps {
  value: string;
  rowIndex: number;
  rows: { spare_part_name: string }[];
}

export const ProductCellRenderer: React.FC<ProductCellRendererProps> = ({
  value,
  rowIndex,
  rows,
}) => {
  const sparePart = rows[rowIndex]?.spare_part_name || "";
  return <ProductCell value={value} sparePart={sparePart} />;
};

// StatusCell.tsx

interface StatusCellProps {
  value: string;
}

export const StatusCell: React.FC<StatusCellProps> = ({ value }) => {
  let bgColor = "";
  switch (value) {
    case "Delivered":
      bgColor = "#e1f2f2";
      break;
    case "Shipped":
      bgColor = "#1976d2";
      break;
    case "Pending":
      bgColor = "#fdf5e1";
      break;
    case "Cancelled":
      bgColor = "#fceddd";
      break;
    default:
      bgColor = "#9e9e9e";
  }
  return (
    <Box
      sx={{
        backgroundColor: bgColor,
        color: "#000",
        padding: "4px 8px",
        borderRadius: "8px",
        textAlign: "center",
        display: "inline-block",
        minWidth: "80px",
      }}
    >
      {value}
    </Box>
  );
};
// TotalPriceCell.tsx

interface TotalPriceCellProps {
  value: string | number;
}

export const TotalPriceCell: React.FC<TotalPriceCellProps> = ({ value }) => (
  <span style={{ fontWeight: "bold" }}>{value}</span>
);

interface RequestStatusCellProps {
  value: string;
}

export const RequestStatusCell: React.FC<RequestStatusCellProps> = ({
  value,
}) => {
  let bgColor = "";
  switch (value) {
    case "Approved":
      bgColor = "#e1f2f2";
      break;
    case "Pending":
      bgColor = "#fdf5e1";
      break;
    case "Rejected":
      bgColor = "#fceddd";
      break;
    default:
      bgColor = "#9e9e9e";
  }
  return (
    <Box
      sx={{
        backgroundColor: bgColor,
        color: "#000",
        padding: "4px 8px",
        borderRadius: "8px",
        textAlign: "center",
        display: "inline-block",
        minWidth: "80px",
      }}
    >
      {value}
    </Box>
  );
};

const formatDate = (dateString: string | number | Date) => {
  const date = new Date(dateString);

  const optionsDate: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  const optionsTime: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  const formattedDate = new Intl.DateTimeFormat("en-US", optionsDate).format(
    date
  );
  const formattedTime = new Intl.DateTimeFormat("en-US", optionsTime).format(
    date
  );

  return `${formattedDate}, ${formattedTime}`;
};
interface Media {
  multimedia_id: number;
  uploaded_by: string;
  media_stage: string | null;
  file_name: string;
  file_type: string;
  file_path: string;
  latitude: number;
  longitude: number;
}
interface Ratings {
  rating_id: number;
  rating: number | null;
  feedback: string | null;
}
interface Ticket {
  multimedia?: Media[] | null;
  title: string;
  status_tracker: string[];
  category_name: string;
  asset_name: string;
  customer_type: string;
  customer_name: string;
  division_name: string;
  customer_phone: string;
  customer_email: string;
  employee_name: string;
  status_name: string;
  address: string;
  city_name: string;
  state_name: string;
  address_type: string;
  description: string;
  rating: Ratings[];
  created_at: string | Date;
  end_date: string | Date;
  feedback: string;
  customer_reject_reason: string;
  pending_reason: string;
  ticket_id: number;
  assignee_history?: string | null;
}
interface Location {
  latitude: number;
  longitude: number;
}
const renderProductCell = (
  value: string,
  tableMeta: { rowIndex: number },
  rows: { spare_part_name: string }[]
) => (
  <ProductCellRenderer
    value={value}
    rowIndex={tableMeta.rowIndex}
    rows={rows}
  />
);
const renderStatusCell = (value: string) => <StatusCell value={value} />;

// Helper for rendering TotalPriceCell
const renderTotalPriceCell = (value: string | number) => (
  <TotalPriceCell value={value} />
);

// Helper for rendering RequestStatusCell
const renderRequestStatusCell = (value: string) => (
  <RequestStatusCell value={value} />
);
const renderDateCell = (value: string) =>
  value ? dayjs(value).format("MMM DD, YYYY") : "-";
const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });
const ViewTickets = () => {
  const { id } = useParams();
  const { fetchError } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const [ticketData, setTicketData] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [assigneeHistory, setAssigneeHistory] = useState<JSX.Element[] | null>(
    null
  );
  const [statusHistory, setStatusHistory] = useState<JSX.Element[] | null>(
    null
  );
  const fetchedIdRef = React.useRef<string | null>(null);

  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    if (!id || fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;
    setLoading(true);
    try {
      const ticketResponse = await axios.get(`/tickets/${id}`);
      const { data } = ticketResponse;
      const ticketData = data?.list;
      if (ticketData?.assignee_history) {
        setAssigneeHistory(FormatAssigneeHistory(ticketData?.assignee_history));
      }
      if (ticketData?.status_history) {
        setStatusHistory(FormatStatusHistoryData(ticketData?.status_history));
      }
      setTicketData(ticketData);
    } catch (err) {
      console.error("Error fetching data", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchedIdRef.current = null;
    };
  }, [fetchData]);
  const isOnDemand = (ticket: { end_date: string | number | Date }) => {
    if (!ticket?.end_date) return true;

    const today = new Date().setHours(0, 0, 0, 0);
    const endDate = new Date(ticket.end_date).setHours(0, 0, 0, 0);

    return endDate < today;
  };
  const handleMediaOpen = (fileName: string) => {
    const url = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${fileName}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleStatusClose = () => {
    setStatusModalOpen(false);
  };
  const handleAssigneeOpen = () => {
    setAssigneeModalOpen(true);
  };

  const handleAssigneeClose = () => {
    setAssigneeModalOpen(false);
  };
  const getLocation = (): Promise<Location> =>
    new Promise((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        (error) => reject(new Error(error.message))
      )
    );
  const [rows, setRows] = React.useState<
    { spare_part_name: string; [key: string]: any }[]
  >([]);

  const [rows1, setRows1] = React.useState([]);

  const columns: MUIDataTableColumn[] = React.useMemo(
    () => [
      {
        name: "asset_type_name",
        label: "Product",
        options: {
          customBodyRender: (value, tableMeta) =>
            renderProductCell(value, tableMeta, rows),
        },
      },

      { name: "order_number", label: "Order ID" },
      {
        name: "order_date",
        label: "Order Date",
        options: {
          customBodyRender: renderDateCell,
        },
      },
      {
        name: "order_status",
        label: "Status",
        options: {
          customBodyRender: renderStatusCell,
        },
      },
      {
        name: "total_price",
        label: "Total",
        options: {
          customBodyRender: renderTotalPriceCell,
        },
      },
    ],
    [rows]
  );

  const columns1: MUIDataTableColumn[] = React.useMemo(
    () => [
      { name: "request_id", label: "Request Id" },
      { name: "asset_type_name", label: "Asset Type" },
      { name: "spare_part_name", label: "Spare Part" },
      { name: "requested_name", label: "Requested By" },
      { name: "quantity", label: "Quantity" },
      {
        name: "status",
        label: "Request Status",
        options: {
          customBodyRender: renderRequestStatusCell,
        },
      },
      {
        name: "request_date",
        label: "Request Date",
        options: {
          customBodyRender: renderDateCell,
        },
      },
    ],
    [rows1]
  );

  const tableOptions: MUIDataTableOptions = {
    selectableRows: "none",
    selectableRowsHeader: false,
    elevation: 0,
    tableBodyHeight: "auto",
    responsive: "standard",
  };
  const fetchSpareOrders = React.useCallback(async () => {
    try {
      const response = await axios.get(`/spare-order/ticket/${id}`);
      setRows(response?.data?.orders || []);
    } catch (error) {
      console.error(error);
    }
  }, [id]);
  React.useEffect(() => {
    if (id) fetchSpareOrders();
  }, [fetchSpareOrders, id]);
  const fetchSpareRequests = React.useCallback(async () => {
    try {
      const response = await axios.get(`/spare-part-request/ticket/${id}`);
      setRows1(response?.data?.requests || []);
    } catch (error) {
      console.error(error);
    }
  }, [id]);
  React.useEffect(() => {
    if (id) fetchSpareRequests();
  }, [fetchSpareRequests, id]);

  const handleEmployeeMediaUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    mediaStage: string
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return; // no file selected

    const file = files[0];

    try {
      const { latitude, longitude } = await getLocation();
      const formData = new FormData();
      const isImage = file.type.startsWith("image/");
      const mediaType = isImage ? "Photo" : "Video";

      formData.append("file", file);
      formData.append("ticket", String(ticketData?.ticket_id ?? ""));
      formData.append("media_type", mediaType);
      formData.append("media_stage", mediaStage); // Make sure this is included
      formData.append("latitude", String(latitude ?? ""));
      formData.append("longitude", String(longitude ?? ""));
      formData.append("uploaded_by", String(user?.userId ?? ""));

      await axios.post("/employee-uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchData();
    } catch (err) {
      console.error("Upload failed:", err);
      fetchError("Upload failed. Try again.");
    }
  };

  if (loading || !ticketData) {
    return <PageLoader />;
  }
  const rawMultimedia = ticketData?.multimedia || [];
  const dedupedMultimedia = rawMultimedia.filter(
    (m, i, arr) => arr.findIndex((x) => x.multimedia_id === m.multimedia_id) === i
  );
  const employeeMedia =
    dedupedMultimedia.filter((media) => media.media_stage !== null);
  const preUploads = employeeMedia.filter((m) => m.media_stage === "pre");
  const postUploads = employeeMedia.filter((m) => m.media_stage === "post");
  const mediaToShow = dedupedMultimedia.filter((m) => m.media_stage === null);
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        className="view-ticket"
        style={{
          padding: "24px",
          width: "100%",
          borderRadius: "8px",
        }}
      >
        <Grid
          item
          xs={12}
          display={"flex"}
          justifyContent={"center"}
          className="view-ticket-title"
        >
          <IconButton className="back-btn">
            <ArrowBackIcon onClick={() => navigate("/admin/tickets-list")} />
          </IconButton>
          <Typography variant="h5" gutterBottom>
            Ticket Details
          </Typography>
        </Grid>
        {ticketData?.status_tracker && (
          <FormatStatusTrackerData trackingData={ticketData.status_tracker} />
        )}
        <Grid container className="view-ticket-details">
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Title</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  : {ticketData?.title}{" "}
                  {isOnDemand(ticketData) && (
                    <span style={{ color: "red", fontWeight: "bold" }}>
                      (On-Demand)
                    </span>
                  )}
                </span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Category</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.category_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Brand Name</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.asset_name}</span>
              </Box>
            </Typography>
          </Grid>
          {(ticketData?.customer_name ||
            ticketData?.customer_phone ||
            ticketData?.customer_email) && (
            <>
              {ticketData?.customer_type === "company" ? (
                <>
                  <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Company Name</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>: {ticketData?.customer_name}</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Division</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>: {ticketData?.division_name}</span>
                      </Box>
                    </Typography>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                    <Typography variant="h6">
                      <Box display="flex" justifyContent="left">
                        <span>Customer Name</span>
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                      <Box display="flex" justifyContent="flex-start">
                        <span>: {ticketData?.customer_name}</span>
                      </Box>
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Customer Mobile</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.customer_phone}</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Customer Email</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.customer_email}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Created At</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :{" "}
                  {ticketData?.created_at
                    ? formatDate(ticketData?.created_at)
                    : ""}
                </span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Assignee</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start" alignItems="center" gap={1} flexWrap="wrap">
                <span>: {ticketData?.employee_name}</span>
                <AiAssignmentBadge
                  assigneeHistory={ticketData?.assignee_history as unknown as string}
                  compact
                />
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Status</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start" alignItems="center" gap={1} flexWrap="wrap">
                <span>: {ticketData?.status_name}</span>
                <AiAssignmentBadge
                  assigneeHistory={ticketData?.assignee_history as unknown as string}
                  compact
                />
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Status History</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <Link
                  sx={{ cursor: "pointer" }}
                  onClick={() => setStatusModalOpen(true)}
                >
                  Click here
                </Link>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Assignee History</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <Link
                  style={{ cursor: "pointer" }}
                  onClick={() => handleAssigneeOpen()}
                >
                  Click here
                </Link>
              </Box>
            </Typography>
          </Grid>
          <Grid container spacing={0}>
            {/* Label */}
            <Grid item xs={12} sm={3} md={2} ml={-1} mr={1}>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                <span>Address</span>
              </Typography>
            </Grid>

            {/* Value */}
            <Grid item xs={12} sm={9} md={10} ml={-1}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "normal",
                  whiteSpace: "pre-wrap", // keep line breaks
                  wordBreak: "break-word", // wrap long words
                }}
              >
                <span>
                  :{" "}
                  {`${ticketData?.address}, ${ticketData?.city_name}, ${ticketData?.state_name}, ${ticketData?.address_type}`}
                </span>
              </Typography>
            </Grid>
          </Grid>

          {/* Description */}
          <Grid item xs={12} sm={12} md={12} sx={{ my: 1 }}>
            <Grid container>
              {/* Label */}
              <Grid item xs={4} sm={4} md={2}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="flex-start">
                    <span>Description</span>
                  </Box>
                </Typography>
              </Grid>
              {/* Value */}
              <Grid item xs={8} sm={8} md={10} ml={-1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "normal",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  : {ticketData?.description}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {ticketData?.rating && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Rating</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>
                      :{" "}
                      <Rating
                        name="simple-controlled"
                        value={ticketData?.rating?.[0]?.rating || 0}
                        readOnly
                      />
                    </span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          {ticketData?.feedback && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Feedback</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.feedback}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          {ticketData?.customer_reject_reason && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Reject Reason</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.customer_reject_reason}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          {ticketData?.pending_reason && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Pending Reason</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.pending_reason}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
        {mediaToShow.length > 0 && (
          <>
            <Divider sx={{ my: 2, height: 2, backgroundColor: "blue" }} />
            <div className="view-ticket-details">
              <Typography variant="h5" gutterBottom>
                Customer Media
              </Typography>
              <div className="upload-media ticket-media">
                {mediaToShow.map((media: Media) => (
                  <div
                    className="media-container view-media"
                    key={media.multimedia_id}
                  >
                    <button
                      type="button"
                      onClick={() => handleMediaOpen(media?.file_name)}
                      style={{
                        all: "unset",
                        cursor: "pointer",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      {media.file_type === "Photo" ? (
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                          alt={`media-${media.multimedia_id}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 140,
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: 140,
                            objectFit: "cover",
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                            type="video/mp4"
                          />
                          <track
                            kind="captions"
                            src=""
                            label="No captions available"
                            default
                          />
                        </video>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {/* Employee Media View - only when pre/post uploads exist */}
        {(preUploads.length > 0 || postUploads.length > 0) && (
          <>
            <Divider sx={{ my: 2, height: 2, backgroundColor: "blue" }} />
            <div className="view-ticket-details">
          <Box sx={{ my: 2 }}>
            <Typography variant="h4">
              <strong>Employee Uploaded Media</strong>
            </Typography>

            <Typography variant="h6" sx={{ pt: 3, pb: 3 }}>
              <strong> Issue Evidence</strong>
            </Typography>
            <Grid container spacing={2}>
              {preUploads.map((media: Media) => (
                <Grid item key={media.multimedia_id} xs={12} sm={6} md={3}>
                  <Card
                    sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}
                  >
                    {media.file_type === "Photo" ? (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                          alt={`pre-${media.multimedia_id}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                          }}
                        />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                            type="video/mp4"
                          />
                          <track
                            kind="captions"
                            src=""
                            label="No captions available"
                            default
                          />
                        </video>
                      </button>
                    )}

                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <LocationOnIcon fontSize="small" color="primary" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        <LocationDisplay
                          latitude={media.latitude}
                          longitude={media.longitude}
                        />
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2, height: 3, backgroundColor: "blue" }} />
            <Typography variant="h6" sx={{ pt: 5, pb: 3 }}>
              <strong> Resolution Evidence</strong>
            </Typography>
            <Grid container spacing={2}>
              {postUploads.map((media: Media) => (
                <Grid item key={media.multimedia_id} xs={12} sm={6} md={3}>
                  <Card
                    sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}
                  >
                    {media.file_type === "Photo" ? (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset", // remove default button styles
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                          alt={`post-${media.multimedia_id}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                          }}
                        />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset", // remove default button styles
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          onClick={() => handleMediaOpen(media.file_name)}
                        >
                          <source
                            src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                            type="video/mp4"
                          />
                          <track
                            kind="captions"
                            src=""
                            label="No captions available"
                            default
                          />
                        </video>
                      </button>
                    )}
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <LocationOnIcon fontSize="small" color="primary" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        <LocationDisplay
                          latitude={media.latitude}
                          longitude={media.longitude}
                        />
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box sx={{ my: 2 }}>
            <Grid container spacing={2}>
              {/* Pre Upload Button */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">Issue Evidence</Typography>
                <input
                  accept="image/*,video/*"
                  id="pre-upload"
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => handleEmployeeMediaUpload(e, "pre")}
                />
                <label htmlFor="pre-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadIcon />}
                  >
                    Upload Pre Media
                  </Button>
                </label>
              </Grid>

              {/* Post Upload Button */}
              <Grid item xs={12} sm={6}>
                <Typography variant="h6">Resolution Evidence</Typography>
                <input
                  accept="image/*,video/*"
                  id="post-upload"
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => handleEmployeeMediaUpload(e, "post")}
                />
                <label htmlFor="post-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadIcon />}
                  >
                    Upload Post Media
                  </Button>
                </label>
              </Grid>
            </Grid>
          </Box>
            </div>
          </>
        )}
        {assigneeModalOpen && (
          <HistoryModal
            title={"Assignee"}
            data={assigneeHistory}
            isOpen={assigneeModalOpen}
            onClose={handleAssigneeClose}
          />
        )}
        {statusModalOpen && (
          <HistoryModal
            title={"Status"}
            data={statusHistory}
            isOpen={statusModalOpen}
            onClose={handleStatusClose}
          />
        )}
      </Card>
      {rows1.length > 0 && (
        <Card sx={{ p: 1, m: "16px auto", maxWidth: "20000px" }}>
          <SoftBox>
            <ThemeProvider theme={getMuiTheme}>
              <MUIDataTable
                title={"Related Requests"}
                data={rows1}
                columns={columns1}
                options={tableOptions}
              />
            </ThemeProvider>
          </SoftBox>
        </Card>
      )}
      {rows.length > 0 && (
        <Card sx={{ p: 1, m: "16px auto", maxWidth: "20000px" }}>
          <SoftBox>
            <ThemeProvider theme={getMuiTheme}>
              <MUIDataTable
                title={"Related Orders"}
                data={rows}
                columns={columns}
                options={tableOptions}
              />
            </ThemeProvider>
          </SoftBox>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default ViewTickets;
